// Date helpers for Day / Week / Month views on the Time Entry page.
// All functions operate in local timezone (not UTC) so "today" matches what the user sees on the wall clock.

/** Format a Date as YYYY-MM-DD in local timezone. */
export const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Parse YYYY-MM-DD to a local-midnight Date. */
export const parseLocalDate = (iso: string): Date => new Date(iso + 'T00:00:00');

/** Sunday of the week containing `date` (start of week). */
export const startOfWeek = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // Sunday = 0
  return d;
};

/** Saturday of the week containing `date` (end of week). */
export const endOfWeek = (date: Date): Date => {
  const s = startOfWeek(date);
  s.setDate(s.getDate() + 6);
  return s;
};

/** First day of the month containing `date`. */
export const startOfMonth = (date: Date): Date => {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Last day of the month containing `date`. */
export const endOfMonth = (date: Date): Date => {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Start of the calendar grid for the month view (back to the nearest prior Sunday). */
export const startOfMonthGrid = (date: Date): Date => startOfWeek(startOfMonth(date));

/** End of the calendar grid for the month view (forward to the nearest following Saturday). */
export const endOfMonthGrid = (date: Date): Date => endOfWeek(endOfMonth(date));

/** All YYYY-MM-DD strings from `start` to `end` inclusive. */
export const datesInRange = (start: Date, end: Date): string[] => {
  const out: string[] = [];
  const cur = new Date(start);
  while (cur <= end) {
    out.push(getLocalDateString(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

/** Add `days` to `date`, returning a new Date. */
export const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/** Add `months` to `date`, returning a new Date. */
export const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

/** Format a week range like "Oct 19 – Oct 25, 2026" (uses the end date's year). */
export const formatWeekRange = (start: Date, end: Date): string => {
  const shortFmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const s = start.toLocaleDateString('en-US', shortFmt);
  const e = end.toLocaleDateString('en-US', shortFmt);
  return `${s} – ${e}, ${end.getFullYear()}`;
};

/** Format a month + year like "October 2026". */
export const formatMonthYear = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
